import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'

export interface LoggedInUserData {
    id:       string
    name:     string
    username: string

    /**
     * Where this person can be reached (docs/OBVESCANJE.md). Carried on the account
     * the shell already reads, so a screen can say "you have no address yet" beside a
     * rota without a second request on every load. The fuller answer — which channels
     * the HOUSE can carry, and the pairing code — is the profile page's own read.
     */
    email:           string
    phone:           string
    telegram_paired: boolean

    /**
     * Whether this person has a PIN for the corridor clock (docs/OSEBJE.md §8). The
     * number itself never comes back — this is only whether there is one.
     *
     * Carried here for the same reason as the contacts above: "Moja ura" offers a
     * button to change it and has to know whether there is one to withdraw, and that
     * screen holds no permission at all, so it cannot ask the account list.
     */
    has_kiosk_pin: boolean
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    // What this account may do, resolved by the server for the property being worked
    // in. Never decided here: the portal hides what it must not offer, and the server
    // refuses it regardless — a hidden button is courtesy, not security.
    const permissions  = ref<string[]>([])

    /**
     * How far this account may discount a line of a folio here, in basis points.
     *
     * It is beside the permissions because it is the same kind of fact — what this
     * account may do in this house — and because the folio screen has to say "tvoja
     * meja: 10 %" BEFORE somebody types a figure. Never decided here: the server
     * resolves it from the roles held and refuses whatever a browser believes.
     *
     * Nought means "no discount at all", which is also what an older service that does
     * not answer the field reads as — the safe direction, since the only cost of
     * being wrong that way is a dialog that says the request will go to a manager.
     */
    const maxDiscountBasisPoints = ref(0)

    /**
     * The modules this hotel has bought (docs/MODULI.md §3.3, HTTP_API/Modules.go).
     *
     * The licence, not a permission: a module is a business whole a customer either
     * has or has not, and the register is read once per session because it belongs to
     * the tenant rather than to the house being worked in.
     *
     * `modulesLoaded` is the difference between "this hotel has no such module" and
     * "nobody has asked yet", and the two must never look alike. Before the answer
     * arrives the portal knows NOTHING, so it hides nothing and refuses nothing —
     * see hasModule. Nothing is authorised here either way: `requireModule` on the
     * service refuses whatever a browser believes.
     */
    const modules       = ref<string[]>([])
    const modulesLoaded = ref(false)

    /**
     * The modules that are being BUILT — announced by the licence, owned by nobody yet.
     *
     * A hotel that clicks a bookmark into one of these is not looking at something it
     * failed to buy; it is looking at something that cannot be bought. The two are the
     * same closed door to the portal and a very different sentence to the customer, so
     * the "module off" page reads this list before it decides which one to say
     * (ModuleOff.vue). Absent from the answer means "no such announcement", never an
     * error: the field arrived after this portal did, and an older service that does
     * not send it simply has nothing in preparation.
     */
    const plannedModules = ref<string[]>([])

    /** The read in flight, so several callers share one — see ensureUserData. */
    let userDataInFlight: Promise<void> | null = null

    // The property the desk is working in. A receptionist works in one hotel all day,
    // so it is chosen once and remembered rather than picked per request.
    const propertyId = ref(localStorage.getItem('propertyId') || '')

    /** True when the account's roles hold in every house, not just in one. */
    const everywhere = ref(false)

    /**
     * Whether this house has a demonstration console at all.
     *
     * It is here rather than in the screen because the MENU has to know it: the Demo
     * desk does not exist for a hotel with real equipment in its rooms — not as a
     * disabled entry, not at all (docs/SOBNA-TELEMETRIJA.md, Logic.TelemetryScenarios).
     * A menu can only leave an entry out if something has already asked, and a menu
     * cannot await.
     *
     * False until the answer comes back, so the entry appears rather than disappears —
     * a menu item that vanishes a second after the page settles is worse than one that
     * arrives a second late.
     *
     * The asking is done by the menu (Sidebar.vue) through the ordinary fetch store and
     * only where the account holds the automation permission. It is not asked here
     * because the fetch store reads THIS store for its token, and a store that called it
     * back would be a cycle.
     */
    const demoConsole = ref(false)

    /** What the menu found out. Signing out forgets it, like every other answer that
     *  belonged to the account rather than to the browser. */
    const setDemoConsole = (available: boolean) => { demoConsole.value = available }

    /**
     * How many proposals the assistant has executed since this page was loaded.
     *
     * The assistant is a drawer over whatever screen is open (libs/assistant.ts), so a
     * confirmation in it changes the page behind it — the booking it just moved, the
     * folio it just discounted, the week it just reshuffled. A screen that goes on
     * showing what it read before the change is a screen the desk stops believing.
     *
     * A counter and not an event, for the reason `onAssistantExecuted` gives: a screen
     * mounted after the execution has already read the new data and must not reload for
     * it. It is here rather than in libs/assistant.ts because the views watch it, and a
     * view already holds this store.
     */
    const assistantExecutions = ref(0)

    /** Called by the assistant once a proposal has actually run. */
    const assistantExecuted = () => assistantExecutions.value++

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Values: 'auto' | 'light' | 'dark'.
    //
    // emitAuto is what makes 'auto' readable. Without it VueUse resolves the stored
    // preference before handing it over, so an account set to 'auto' on a device whose
    // system is dark reads back as 'dark' — and the three-state button in the topbar
    // could never leave: it saw 'dark', wrote 'auto', read 'dark' again, and a
    // housekeeper on a dark iPad pressed it all day for nothing. On a light device the
    // same code appeared to work, because there 'auto' reads back as 'light' and the
    // cycle at least moved. The class on <html> still follows the RESOLVED mode; only
    // what we read here is the preference itself.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme', emitAuto: true })

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value

    /** Whether this account holds a permission in the property it is working in. */
    const can = (permission: string): boolean => permissions.value.includes(permission)

    /** Whether it holds any of them — for a screen that several permissions reach. */
    const canAny = (...wanted: string[]): boolean => wanted.some(can)

    /**
     * Whether this hotel holds a module.
     *
     * True while the licence is still unknown, and that direction is deliberate. A
     * menu that hides every module entry for the half second before the answer
     * arrives, and then puts them back, is worse than one that draws them a moment
     * early; and the one place where being early would actually hurt — the router
     * sending somebody to the "module not enabled" page — awaits the read first
     * (ensureUserData), so it never asks this question while the answer is unknown.
     */
    const hasModule = (key: string): boolean => !modulesLoaded.value || modules.value.includes(key)

    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /**
     * Signing out, and the cookie the server holds.
     *
     * The session cookie is written by the server and is HttpOnly, so this page cannot
     * remove it — which is the whole point of it being HttpOnly, and the reason signing
     * out has to ask the server. It is asked and not waited on: whatever the network
     * says, this browser is signed out the moment somebody presses the button.
     */
    const clearServerSession = () => {
        void window.fetch('/api/pms/v1/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
    }

    /**
     * Reads the licence — which modules this hotel has bought.
     *
     * Once per page load, and then again whenever somebody asks with `force`: the
     * answer belongs to the tenant, not to the house being worked in, so switching
     * property does not change it and signing out is what forgets it. What DOES change
     * it is the vendor selling a module to a hotel whose desk left the portal open all
     * week — see `useModuleRefresh`, which is the only caller that forces. Read with
     * the same raw call and the same header as `/me` above, for the same reason: the
     * fetch store takes its token from THIS store, so a store that called it back
     * would be a cycle.
     *
     * A failed read leaves the licence unknown rather than empty, and a REFRESH that
     * fails leaves the licence it already had. An empty licence would hide every module
     * in the menu of a hotel that has paid for all of them, on nothing worse than one
     * dropped request.
     */
    const loadModules = async (force = false): Promise<void> => {
        if (!JWT.value) return
        if (modulesLoaded.value && !force) return

        const response = await window.fetch('/api/pms/v1/modules', {
            headers: { Authorization: `Bearer ${JWT.value}` },
        })

        if (!response.ok) return

        const body = await response.json() as { modules?: string[] | null, planned?: string[] | null }

        modules.value        = body.modules ?? []
        plannedModules.value = body.planned ?? []
        modulesLoaded.value  = true
    }

    /**
     * Whether a module this hotel does not hold is one nobody holds yet.
     *
     * Only ever asked about a module already found missing, so it does not repeat
     * `hasModule`'s "unknown means yes": while the licence is unknown nothing is
     * planned either, and the page that asks has not been reached.
     */
    const isModulePlanned = (key: string): boolean => plannedModules.value.includes(key)

    /**
     * Reads the signed-in account and applies what belongs to the person rather than
     * to the browser. The language lives on the account so that somebody signing in
     * at the second terminal is not greeted in the language the last person there
     * chose; an account with none set leaves the browser's choice alone.
     */
    const loadUserData = async (retrying = false): Promise<void> => {
        if (!JWT.value) return

        // Scoped to the property being worked in: the same person can be a
        // housekeeper in one house and nothing at all in another.
        const query = propertyId.value ? `?property_id=${propertyId.value}` : ''

        const response = await window.fetch(`/api/pms/v1/me${query}`, {
            headers: { Authorization: `Bearer ${JWT.value}` },
        })

        if (!response.ok) return

        // The contact half is optional in the CAST although it is not on the type: a
        // portal talking to a service that predates it must not crash on three missing
        // fields, and the defaults below are what "we have no address for you" is.
        const user = await response.json() as Omit<LoggedInUserData,
            'email' | 'phone' | 'telegram_paired' | 'has_kiosk_pin'> & {
            language?:        string
            permissions?:     string[]
            max_discount_bp?: number
            properties?:      { id: string; name: string }[]
            assignments?:     { role_id: string; property_id: string }[]
            email?:           string
            phone?:           string
            telegram_paired?: boolean
            has_kiosk_pin?:   boolean
        }

        userData.value = {
            id:              user.id,
            name:            user.name,
            username:        user.username,
            email:           user.email ?? '',
            phone:           user.phone ?? '',
            telegram_paired: user.telegram_paired ?? false,
            has_kiosk_pin:   user.has_kiosk_pin ?? false,
        }

        permissions.value = user.permissions ?? []

        maxDiscountBasisPoints.value = user.max_discount_bp ?? 0

        // The licence, immediately after the account and on the same authority. The
        // menu and the router both need it before they draw anything.
        await loadModules()

        /*
         * Whether this account's roles apply in EVERY house of the company, or only in
         * the one it was assigned to. A zero property on an assignment means everywhere.
         *
         * It decides one thing in the menu and nothing else: the screens that are not
         * under a house — the list of houses itself, the accounts — resolve their
         * permissions with no house named, so an account assigned to a single house
         * holds none of them there and the service refuses. Drawing those entries for
         * such a person promises a screen that answers 403, which is exactly what the
         * menu's own rule forbids.
         */
        everywhere.value = (user.assignments ?? []).some(a => /^0+$/.test(a.property_id))

        if (user.language && user.language !== locale.value) changeLocale(user.language)

        /*
         * The first sign-in of somebody whose ROLE is tied to one house.
         *
         * `/me` with no property answers with the company-wide permissions alone, which
         * is right and deliberate. But an account assigned to a single house holds none
         * of those — so the first answer after a sign-in is an empty set, the portal
         * draws no menu, and it draws no property switcher either, which is the trap:
         * the permissions need a house and the house needs a screen that permissions
         * would have drawn.
         *
         * The answer already carries the houses the account works in, whatever the
         * permissions came back as. So when we asked unscoped and got houses, adopt one
         * and ask again — the only one where there is only one, and otherwise the first,
         * which the switcher can still change.
         *
         * Once, and only when the first ask carried no scope: `retrying` is what stops a
         * house that answers with nothing from turning this into a loop.
         */
        if (retrying || propertyId.value) return

        const houses = user.properties ?? []
        if (!houses.length) return

        setPropertyId(houses[0].id)

        await loadUserData(true)
    }

    /**
     * The one read of the account and the licence a page load makes.
     *
     * Two callers want it at the same moment on every reload — the router's guard,
     * which must not decide a module question before the licence is in, and the app
     * shell, which reads the account because a reload does not pass the login screen.
     * Without this they asked twice, and the second answer could undo the house the
     * first had just adopted.
     *
     * It never rejects: a portal that cannot reach the service still has to render,
     * and an unknown licence gates nothing (see hasModule).
     */
    const ensureUserData = (): Promise<void> => {
        if (modulesLoaded.value) return Promise.resolve()

        if (!userDataInFlight) {
            userDataInFlight = loadUserData()
                .catch(() => {})
                .finally(() => { userDataInFlight = null })
        }

        return userDataInFlight
    }

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        permissions.value = []
        // The ceiling belonged to the account's roles, like the permissions above it.
        maxDiscountBasisPoints.value = 0
        // The licence belonged to the account's tenant, not to this browser: the next
        // person to sign in here may be in another hotel entirely.
        modules.value = []
        plannedModules.value = []
        modulesLoaded.value = false
        userDataInFlight = null
        demoConsole.value = false
        clearServerSession()
    }

    const setPropertyId = (id: string) => {
        propertyId.value = id
        localStorage.setItem('propertyId', id)
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    return {
        JWT, locale, userData, permissions, maxDiscountBasisPoints, loaderActive, colorMode, propertyId,
        demoConsole, everywhere, assistantExecutions,
        modules, modulesLoaded, plannedModules,
        getJWT, isLoggedIn, isLoaderActive, isMobile, can, canAny, hasModule, isModulePlanned, setDemoConsole,
        assistantExecuted,
        setLogin, setLogout, setPropertyId, startLoading, stopLoading, changeLocale, loadUserData, ensureUserData,
        loadModules,
    }
})
